import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPrograms } from "../../../redux/program/programActions";
import { fetchSubjects } from "../../../redux/subject/subjectActions";
import { fetchEvaluationTypes } from "../../../redux/evaluationType/evaluationTypeActions";
import { enqueueSnackbar } from "notistack";

import {
  getProgramWiseSubjectList,
  getProgramWiseExamList,
} from "../../../services/ExamService";

import {
  getExamConfigs,
  getFilteredExamConfigs,
  deleteExamConfig,
} from "../../../services/ExamConfigService";
import { getSubjects } from "../../../services/SubjectServices";
import { getAssessmentLists } from "../../../services/AssessmentListService";

export const useExamConfigList = () => {
  const dispatch = useDispatch();

  // Global stored data
  const programData = useSelector((s) => s.program.programs.data);
  const evaluationTypes = useSelector((s) => s.evaluationType.evaluationTypes.data);

  // UI States
  const [examConfigData, setExamConfigData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [examList, setExamList] = useState([]);

  const [programs, setPrograms] = useState([]);
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [searchMode, setSearchMode] = useState(false);

  // Pagination
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  // Filters
  const [filters, setFilters] = useState({
    subject_id: "",
    evaluation_type_id: "",
    program_id: "",
    name: "",
    start: "",
    end: "",
    term: "",
    status: "",
  });

  // Load initial data
  useEffect(() => {
    dispatch(fetchPrograms());
    dispatch(fetchSubjects({ selected: 0 }));
    dispatch(fetchEvaluationTypes());
    loadExamConfigList(1);
    loadAllSubjectsAndExams();
  }, [dispatch]);

  /**
   * Load all subjects and exam headers so filters work without selecting a class
   */
  const loadAllSubjectsAndExams = async () => {
    try {
      const [subjectRes, examRes] = await Promise.all([
        getSubjects(),
        getAssessmentLists(),
      ]);
      setSubjectData(subjectRes?.data || []);
      setExamList(examRes?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Load list normally (without filters)
   */
  const loadExamConfigList = async (page) => {
    try {
      setIsLoader(true);
      setSearchMode(false);
      setCurrentPage(page);

      const res = await getExamConfigs(page);
      if (res.status === 200) {
        setExamConfigData(res.data.data);
        setLastPage(res.data.last_page);
        setPerPage(res.data.per_page);
      }
    } finally {
      setIsLoader(false);
    }
  };

  /**
   * Load filtered exam config list
   */
  const searchExamConfigList = useCallback(
    async (page) => {
      try {
        setIsLoader(true);
        setSearchMode(true);
        setCurrentPage(page);

        let program_ids = programs.map((p) => p.id);

        const res = await getFilteredExamConfigs(
          filters.name,
          program_ids,
          filters.subject_id,
          filters.evaluation_type_id,
          filters.start,
          filters.end,
          filters.term,
          filters.status,
          page
        );

        if (res.status === 200) {
          setExamConfigData(res.data.data);
          setLastPage(res.data.last_page);
          setPerPage(res.data.per_page);
        }
      } finally {
        setIsLoader(false);
      }
    },
    [filters, programs]
  );

  /**
   * Filters change handler
   */
  const onFilterChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    setFilters({
      subject_id: "",
      evaluation_type_id: "",
      program_id: "",
      name: "",
      start: "",
      end: "",
      term: "",
      status: "",
    });
    setPrograms([]);
    loadExamConfigList(1);
    loadAllSubjectsAndExams();
  };

  /**
   * Delete record
   */
  const deleteExam = async (id) => {
    try {
      const res = await deleteExamConfig(id);
      if (res.status === 200) {
        enqueueSnackbar("Exam deleted successfully", { variant: "success" });
        loadExamConfigList(1);
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  /**
   * Program selection
   */
  const onProgramSelect = async (items) => {
    setPrograms(items);

    if (items.length === 0) {
      loadAllSubjectsAndExams();
      return;
    }

    const program_id = items[0].id;

    const subjectRes = await getProgramWiseSubjectList(program_id);
    setSubjectData(subjectRes?.data?.data || []);

    const examRes = await getProgramWiseExamList(program_id);
    setExamList(examRes?.data?.data || []);
  };

  return {
    // Data
    examConfigData,
    subjectData,
    programData,
    examList,
    evaluationTypes,
    programs,

    // UI
    isLoader,
    advancedSearch,
    perPage,
    currentPage,
    lastPage,
    searchMode,

    // Filters
    filters,
    onFilterChange,
    resetFilters,

    // Actions
    deleteExam,
    onProgramSelect,

    // Loaders
    loadExamConfigList,
    searchExamConfigList,

    toggleAdvancedSearch: () => setAdvancedSearch((p) => !p),
  };
};
